import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { useCookies } from 'react-cookie';
import StoreContext from '../../components/Store/context';
import { POSProvider, usePOS } from '../../contexts/POSContext';
import POSHeader from '../../components/PDV/POSHeader';
import POSScannerBar from '../../components/PDV/POSScannerBar';
import POSCartTable from '../../components/PDV/POSCartTable';
import POSSummaryPanel from '../../components/PDV/POSSummaryPanel';
import POSBottomBar from '../../components/PDV/POSBottomBar';
import POSSearchModal from '../../components/PDV/Modals/POSSearchModal';
import POSCustomerModal from '../../components/PDV/Modals/POSCustomerModal';
import POSCouponModal from '../../components/PDV/Modals/POSCouponModal';
import POSDiscountModal from '../../components/PDV/Modals/POSDiscountModal';
import POSCheckoutModal from '../../components/PDV/Modals/POSCheckoutModal';
import POSFullscreenPromptModal from '../../components/PDV/Modals/POSFullscreenPromptModal';
import CashierSelect from '../../components/Cashier/Select/cashierSelect';
import UIModalCashierOpen from '../../components/UI/Modal/UIModalCashierOpen';
import '../../components/PDV/pdv.css';

const POSView = () => {
  const { credencials, modalCashierSelect, setModalCashierSelect } = useContext(StoreContext);
  const { cartItems, isDarkMode, activeCashier } = usePOS();
  const [cookies] = useCookies(['duzz_pdv_fullscreen_pref']);
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Desativa o modo PDV no mobile e redireciona para a tela de venda padrão
  useEffect(() => {
    if (isMobile) {
      history.replace('/home/vendas/nova');
    }
  }, [isMobile, history]);

  // Estados dos modais
  const [openSearch, setOpenSearch] = useState(false);
  const [searchTermForModal, setSearchTermForModal] = useState('');
  const [openCustomer, setOpenCustomer] = useState(false);
  const [openCoupon, setOpenCoupon] = useState(false);
  const [openDiscount, setOpenDiscount] = useState(false);
  const [openCheckout, setOpenCheckout] = useState(false);
  const [openFullscreenPrompt, setOpenFullscreenPrompt] = useState(false);

  // Verificação e prompt de Fullscreen
  useEffect(() => {
    if (isMobile) return;

    const pref = cookies.duzz_pdv_fullscreen_pref;

    if (!document.fullscreenElement) {
      if (pref === 'always') {
        // Tenta ativar no primeiro clique do usuário
        const handleFirstInteraction = () => {
          if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(() => {});
          }
          window.removeEventListener('click', handleFirstInteraction);
        };
        window.addEventListener('click', handleFirstInteraction, { once: true });
      } else if (pref !== 'never') {
        // Se ainda não escolheu "never", pergunta gentilmente
        const timer = setTimeout(() => {
          if (!document.fullscreenElement) {
            setOpenFullscreenPrompt(true);
          }
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [cookies.duzz_pdv_fullscreen_pref, isMobile]);

  // Verificação do Caixa da Sessão ao abrir o PDV
  useEffect(() => {
    if (!credencials?.cashSession || credencials.cashSession === 0) {
      setModalCashierSelect(true);
    } else if (activeCashier) {
      const isClosed =
        activeCashier.isOpen === '0' ||
        activeCashier.isOpen === 0 ||
        activeCashier.isOpen === false ||
        !activeCashier.isOpen;
      if (isClosed) {
        history.push('/home/caixas/detalhes');
      }
    }
  }, [credencials?.cashSession, activeCashier, setModalCashierSelect, history]);

  const handleOpenSearchWithTerm = (term = '') => {
    setSearchTermForModal(term);
    setOpenSearch(true);
  };

  // Manipulador de Atalhos de Teclado Globais (F2, F7, F8, F9, F10, Shift+Enter, Esc)
  const handleGlobalKeyDown = useCallback((e) => {
    const isAnyModalOpen =
      openSearch ||
      openCustomer ||
      openCoupon ||
      openDiscount ||
      openCheckout ||
      modalCashierSelect;

    if (e.key === 'F2') {
      e.preventDefault();
      handleOpenSearchWithTerm('');
    } else if (e.key === 'F7') {
      e.preventDefault();
      setOpenCustomer(true);
    } else if (e.key === 'F8') {
      e.preventDefault();
      setOpenCoupon(true);
    } else if (e.key === 'F9') {
      e.preventDefault();
      setOpenDiscount(true);
    } else if (e.key === 'F10' || (e.shiftKey && e.key === 'Enter')) {
      e.preventDefault();
      if (cartItems.length > 0) setOpenCheckout(true);
    } else if (e.key === 'Escape' && !isAnyModalOpen) {
      const barcodeEl = document.getElementById('sale-pos-barcode-input');
      if (barcodeEl && document.activeElement !== barcodeEl) {
        barcodeEl.focus();
      } else {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        }
        history.push('/home');
      }
    }
  }, [openSearch, openCustomer, openCoupon, openDiscount, openCheckout, modalCashierSelect, cartItems.length, history]);

  useEffect(() => {
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleGlobalKeyDown]);

  return (
    <div className={`pos-root-container ${isDarkMode ? 'pos-dark-theme' : ''}`}>
      {/* Header com Logo Oficial, Caixa, Operador, Sync e Ações */}
      <POSHeader />

      {/* Corpo Principal: Estrutura em Grid 71% / 29% */}
      <div className="pos-main-body">
        {/* Coluna Esquerda: Entrada de Item e Tabela do Carrinho */}
        <div className="pos-left-column">
          <POSScannerBar
            onOpenSearch={handleOpenSearchWithTerm}
            onOpenCustomer={() => setOpenCustomer(true)}
            onOpenCoupon={() => setOpenCoupon(true)}
            onOpenDiscount={() => setOpenDiscount(true)}
            onOpenCheckout={() => {
              if (cartItems.length > 0) setOpenCheckout(true);
            }}
          />
          <POSCartTable />
        </div>

        {/* Coluna Direita: Opções da Venda (Cliente, Desconto, Parcelas) */}
        <div className="pos-right-column">
          <POSSummaryPanel
            onOpenCustomer={() => setOpenCustomer(true)}
            onOpenCoupon={() => setOpenCoupon(true)}
            onOpenDiscount={() => setOpenDiscount(true)}
          />
        </div>
      </div>

      {/* Barra de Rodapé: Total e Fechamento */}
      <POSBottomBar
        onOpenCheckout={() => setOpenCheckout(true)}
      />

      {/* Modais Rápidos */}
      <POSSearchModal
        open={openSearch}
        initialSearchTerm={searchTermForModal}
        onClose={() => setOpenSearch(false)}
      />

      <POSCustomerModal
        open={openCustomer}
        onClose={() => setOpenCustomer(false)}
      />

      <POSCouponModal
        open={openCoupon}
        onClose={() => setOpenCoupon(false)}
      />

      <POSDiscountModal
        open={openDiscount}
        onClose={() => setOpenDiscount(false)}
      />

      <POSCheckoutModal
        open={openCheckout}
        onClose={() => setOpenCheckout(false)}
      />

      {/* Prompt de Tela Cheia */}
      <POSFullscreenPromptModal
        open={openFullscreenPrompt}
        onClose={() => setOpenFullscreenPrompt(false)}
      />

      {/* Modal de Seleção de Caixa da Sessão */}
      <CashierSelect
        open={modalCashierSelect}
        handleModal={() => setModalCashierSelect(!modalCashierSelect)}
        isDark={isDarkMode}
      />
      <UIModalCashierOpen />
    </div>
  );
};

const PDV = () => {
  return (
    <POSProvider>
      <POSView />
    </POSProvider>
  );
};

export default PDV;
